import React, { Fragment, lazy, useMemo } from 'react';

import { graphql, loadQuery, usePreloadedQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { RelayEnvironment } from '@atlassian/atlassian-studio-common';
import { getMappedEnv } from '@atlassian/atlassian-studio-env-utils';
import {
	extractWorkspaceOrSiteIdFromPath,
	isStudioV2,
	setIsStudioV2,
	type URLContext,
} from '@atlassian/atlassian-studio-routing';
import { PageMainContentLoadingLogo } from '@atlassian/atlassian-studio-ui-components';
import {
	featureGatesOptions,
	useInitialiseFeatureGatesClient,
} from '@atlassian/studio-foundations-feature-gates';

import AppSelectorQuery, {
	type AppSelectorQuery as AppSelectorQueryType,
} from './__generated__/AppSelectorQuery.graphql';
import { useWorkspaceContext } from './hooks/use-workspace-context';

const LazyDevToolsDialogAndToggle = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_studio-foundations-feature-gates" */ '@atlassian/studio-foundations-feature-gates'
	).then((module) => ({
		default: module.DevToolsDialogAndToggle,
	})),
);

const LazyLegacyAtlassianStudioApp = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_atlassian-studio" */ '@atlassian/atlassian-studio'
	).then((module) => ({
		default: module.AtlassianStudioApp,
	})),
);

const LazyStudioFoundationsApp = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_studio-foundations" */ './StudioFoundationsApp'
	).then((module) => ({
		default: module.StudioFoundationsApp,
	})),
);

const routerQueryReference = loadQuery<AppSelectorQueryType>(
	RelayEnvironment,
	AppSelectorQuery,
	{},
);

const isProd = getMappedEnv() === 'production';

export const AppSelector = (): React.JSX.Element => {
	const data = usePreloadedQuery(
		graphql`
			query AppSelectorQuery {
				me {
					user @required(action: THROW) {
						accountId @required(action: THROW)
						...StudioFoundationsApp_AtlassianStudioFoundations_userDataFragment
					}
				}
				notifications {
					...StudioFoundationsApp_AtlassianStudioFoundations_notificationDataFragment
				}
			}
		`,
		routerQueryReference,
	);

	const userAccountId = data.me.user.accountId;
	const { initialized: fgPartlyInitialized, failReason: fgInitFailReason } =
		useInitialiseFeatureGatesClient(
			{
				atlassianAccountId: userAccountId,
			},
			featureGatesOptions,
		);

	const isAppSelectionChangeEnabled =
		fgPartlyInitialized && fg('asf620_route_arch_and_app_selection_changes');
	const urlContext: URLContext = useMemo(
		() =>
			isAppSelectionChangeEnabled
				? extractWorkspaceOrSiteIdFromPath()
				: { workspaceId: undefined, siteId: undefined },
		[isAppSelectionChangeEnabled],
	);

	// Gather workspace context
	const workspaceContext = useWorkspaceContext(urlContext, fgPartlyInitialized);

	// @NOTE: if the feature gates are not yet initialized, and there is no
	// failure reason, then show the loading spinner
	// if they do fail, for some reason, just proceed so we dont block the user
	if (!fgPartlyInitialized && !fgInitFailReason) {
		return (
			<PageMainContentLoadingLogo
				testId="studio-foundations.app-selector.loading-logo"
				interactionName="studio-foundations-app-selector"
			/>
		);
	}
	//

	// Gate all the routing logic behind feature flag
	if (isAppSelectionChangeEnabled) {
		// show loading while gathering workspace context
		if (!workspaceContext.isReady) {
			return (
				<PageMainContentLoadingLogo
					testId="studio-foundations.app-selector.loading-logo"
					interactionName="studio-foundations-app-selector"
				/>
			);
		}

		// set v2 flag based on workspace context. Note that redirect would've already happened if it was necessary.
		const isV2Value = workspaceContext.workspaces.length > 0;
		setIsStudioV2(isV2Value);
	}

	return (
		<Fragment>
			{isStudioV2() ? (
				<LazyStudioFoundationsApp
					userDataFragment={data.me.user}
					notificationDataFragment={data.notifications ?? undefined}
				/>
			) : (
				<LazyLegacyAtlassianStudioApp />
			)}
			{/* @NOTE: deploy the ui dev tools modal just once for both v1 and v2 applications */}
			{!isProd ? <LazyDevToolsDialogAndToggle /> : null}
		</Fragment>
	);
};
